import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { FiCamera, FiUpload, FiCheck, FiAlertCircle, FiClock, FiLoader, FiX } from 'react-icons/fi';
import { scannerService } from '../services/api';
import './Scanner.css';

const readerId = 'scanner-qr-reader';

function ScannerMembersBadge({ count, variant }) {
  const n = count != null && count !== '' ? count : '—';
  const cls = ['scanner-members-badge', variant && `scanner-members-badge--${variant}`]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls} role="status" aria-label={`Total allowed members: ${n}`}>
      <span className="scanner-members-badge__label">Total allowed members</span>
      <span className="scanner-members-badge__value">{n}</span>
    </div>
  );
}

/** Which minimal UI to show in the validation popup */
function validationFlow(result) {
  if (!result) return null;
  const c = result.code;
  if (c === 'visited_ok') return 'approved';
  if (c === 'valid') return 'valid';
  if (c === 'already_used') return 'already_used';
  if (c === 'not_yet_valid') return 'future';
  if (c === 'visit_date_passed') return 'past';
  return 'other';
}

function ScannerValidationPopup({
  result,
  booking,
  error,
  hasErrorOnly,
  showAccept,
  showExpiredBtn,
  actionLoading,
  onApprove,
  onMarkExpired,
  onClose,
}) {
  if (hasErrorOnly) {
    return (
      <div className="scanner-popup scanner-popup--error">
        <div className="scanner-popup-icon scanner-popup-icon--warn" aria-hidden>
          <FiAlertCircle strokeWidth={2.5} />
        </div>
        <p className="scanner-popup-text">{error}</p>
        <div className="scanner-popup-actions scanner-popup-actions--single">
          <button type="button" className="scanner-btn scanner-btn--secondary scanner-btn--popup" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const flow = validationFlow(result);

  if (flow === 'approved') {
    return (
      <div className="scanner-popup scanner-popup--approved">
        <div className="scanner-popup-icon scanner-popup-icon--success" aria-hidden>
          <FiCheck strokeWidth={2.5} />
        </div>
        <p className="scanner-popup-text scanner-popup-text--success">{result.message}</p>
        {booking && <ScannerMembersBadge count={booking.number_of_visitors} variant="approved" />}
        <div className="scanner-popup-actions scanner-popup-actions--single">
          <button type="button" className="scanner-btn scanner-btn--primary scanner-btn--popup" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (flow === 'valid') {
    return (
      <div className="scanner-popup scanner-popup--valid">
        <div className="scanner-popup-icon scanner-popup-icon--success" aria-hidden>
          <FiCheck strokeWidth={2.5} />
        </div>
        <p className="scanner-popup-text scanner-popup-text--success">{result.message}</p>
        {booking && (
          <>
            <ScannerMembersBadge count={booking.number_of_visitors} />
            <div className="scanner-popup-minimal">
              <div className="scanner-popup-line">
                <span>Name</span>
                <strong>{booking.full_name || '—'}</strong>
              </div>
              <div className="scanner-popup-line">
                <span>Ticket ID</span>
                <strong className="scanner-popup-id">{booking.booking_id}</strong>
              </div>
            </div>
          </>
        )}
        <div className="scanner-popup-actions">
          {showAccept ? (
            <button
              type="button"
              className="scanner-btn scanner-btn--success scanner-btn--popup"
              onClick={onApprove}
              disabled={actionLoading}
            >
              {actionLoading ? <FiLoader className="scanner-spinner-inline" aria-hidden /> : null}
              Approve
            </button>
          ) : (
            <button type="button" className="scanner-btn scanner-btn--secondary scanner-btn--popup" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  if (flow === 'already_used') {
    return (
      <div className="scanner-popup scanner-popup--blocked">
        <div className="scanner-popup-icon scanner-popup-icon--reject" aria-hidden>
          <FiX strokeWidth={2.5} />
        </div>
        <p className="scanner-popup-text scanner-popup-text--error">This ticket has already been used.</p>
        <div className="scanner-popup-actions scanner-popup-actions--single">
          <button type="button" className="scanner-btn scanner-btn--secondary scanner-btn--popup" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (flow === 'future') {
    return (
      <div className="scanner-popup scanner-popup--future">
        <div className="scanner-popup-icon scanner-popup-icon--future" aria-hidden>
          <FiClock strokeWidth={2.5} />
        </div>
        <p className="scanner-popup-text">This is a future ticket and is not yet valid.</p>
        <div className="scanner-popup-actions scanner-popup-actions--single">
          <button type="button" className="scanner-btn scanner-btn--secondary scanner-btn--popup" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (flow === 'past') {
    return (
      <div className="scanner-popup scanner-popup--past">
        <div className="scanner-popup-icon scanner-popup-icon--warn" aria-hidden>
          <FiAlertCircle strokeWidth={2.5} />
        </div>
        <p className="scanner-popup-text">{result.message}</p>
        <div className="scanner-popup-actions">
          {showExpiredBtn && booking?.status === 'payment_completed' && (
            <button
              type="button"
              className="scanner-btn scanner-btn--danger scanner-btn--popup"
              onClick={onMarkExpired}
              disabled={actionLoading}
            >
              Mark expired
            </button>
          )}
          <button type="button" className="scanner-btn scanner-btn--secondary scanner-btn--popup" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="scanner-popup scanner-popup--other">
      <div className="scanner-popup-icon scanner-popup-icon--warn" aria-hidden>
        <FiAlertCircle strokeWidth={2.5} />
      </div>
      <p className="scanner-popup-text">{result.message}</p>
      {booking && (
        <>
          <ScannerMembersBadge count={booking.number_of_visitors} variant="neutral" />
          <div className="scanner-popup-minimal">
            <div className="scanner-popup-line">
              <span>Name</span>
              <strong>{booking.full_name || '—'}</strong>
            </div>
          </div>
        </>
      )}
      <div className="scanner-popup-actions scanner-popup-actions--single">
        <button type="button" className="scanner-btn scanner-btn--secondary scanner-btn--popup" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

function useScanBoxSize(readerWrapRef) {
  const [size, setSize] = useState(280);

  useEffect(() => {
    const node = readerWrapRef.current;
    if (!node) return undefined;
    const update = (el) => {
      const w = el.clientWidth;
      setSize(Math.max(200, Math.min(Math.floor(w - 24), 340)));
    };
    update(node);
    const ro = new ResizeObserver((entries) => {
      const el = entries[0]?.target;
      if (el) update(el);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [readerWrapRef]);

  return size;
}

export default function Scanner() {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [manualId, setManualId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const html5Ref = useRef(null);
  const fileInputRef = useRef(null);
  const readerWrapRef = useRef(null);
  const modalCloseRef = useRef(null);
  const scanBox = useScanBoxSize(readerWrapRef);

  const qrConfig = useMemo(
    () => ({
      fps: 12,
      qrbox: { width: scanBox, height: scanBox },
      aspectRatio: 1,
    }),
    [scanBox]
  );

  const stopCamera = useCallback(async () => {
    try {
      if (html5Ref.current) {
        try {
          await html5Ref.current.stop();
        } catch (e) {
          /* ignore if not running */
        }
        try {
          await html5Ref.current.clear();
        } catch (e) {
          /* ignore */
        }
        html5Ref.current = null;
      }
    } catch (e) {
      console.warn('stopCamera', e);
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setResult(null);
    setError('');
  }, []);

  const verifyPayload = async (raw) => {
    setError('');
    setLoading(true);
    setResult(null);
    setModalOpen(false);
    try {
      const { data } = await scannerService.verify({ qr_payload: raw });
      setResult(data);
      setModalOpen(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Verification failed');
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const startCamera = async () => {
    setError('');
    await stopCamera();
    const el = document.getElementById(readerId);
    if (!el) return;
    try {
      const h = new Html5Qrcode(readerId);
      html5Ref.current = h;
      await h.start(
        { facingMode: 'environment' },
        qrConfig,
        (decodedText) => {
          verifyPayload(decodedText);
          stopCamera();
        },
        () => {}
      );
      setScanning(true);
    } catch (e) {
      setError(e.message || 'Could not start camera. Check permissions or use file upload.');
      setModalOpen(true);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    await stopCamera();
    const h = new Html5Qrcode(readerId);
    try {
      const text = await h.scanFile(file, true);
      await verifyPayload(text);
    } catch (err) {
      setError(err.message || 'Could not read QR from image');
      setModalOpen(true);
    }
  };

  const onManualVerify = () => {
    if (!manualId.trim()) return;
    verifyPayload(manualId.trim());
  };

  const markVisited = async () => {
    const bid = result?.booking?.booking_id;
    if (!bid) return;
    setActionLoading(true);
    setError('');
    try {
      const { data } = await scannerService.markVisited({ booking_id: bid });
      setResult((r) => ({
        ...r,
        ok: true,
        code: 'visited_ok',
        message: 'Ticket marked as visited.',
        booking: data.booking,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const markExpired = async () => {
    const bid = result?.booking?.booking_id;
    if (!bid) return;
    setActionLoading(true);
    setError('');
    try {
      const { data } = await scannerService.markExpired({ booking_id: bid });
      setResult((r) => ({
        ...r,
        ok: false,
        code: 'expired_status',
        message: 'Ticket marked as expired.',
        booking: data.booking,
      }));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const booking = result?.booking;
  const showAccept =
    result?.can_accept &&
    result?.code === 'valid' &&
    booking?.status === 'payment_completed';
  const showExpiredBtn = result?.can_mark_expired || result?.code === 'visit_date_passed';

  const showReaderPlaceholder = !scanning && !loading;

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalCloseRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [modalOpen, closeModal]);

  return (
    <main className="scanner-page" aria-busy={loading || actionLoading}>
      <header className="scanner-header">
        <h1 className="scanner-title">Ticket Scanner</h1>
        <p className="scanner-sub">Point the camera at a QR code, upload an image, or enter a booking ID.</p>
      </header>

      <div className="scanner-layout">
        <section className="scanner-scan-section" aria-labelledby="scanner-scan-heading">
          <h2 id="scanner-scan-heading" className="scanner-visually-hidden">
            Scan or enter booking
          </h2>

          <div className="scanner-camera-card">
            <div className="scanner-controls" role="toolbar" aria-label="Scanning options">
              <button
                type="button"
                className="scanner-btn scanner-btn--primary"
                onClick={startCamera}
                disabled={scanning || loading}
                aria-pressed={scanning}
              >
                <FiCamera aria-hidden />
                <span>{scanning ? 'Camera on' : 'Use camera'}</span>
              </button>
              {scanning && (
                <button type="button" className="scanner-btn scanner-btn--secondary" onClick={stopCamera}>
                  Stop
                </button>
              )}
              <button
                type="button"
                className="scanner-btn scanner-btn--secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
              >
                <FiUpload aria-hidden />
                <span>Upload image</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="scanner-file-input"
                onChange={onFileChange}
                aria-label="Upload QR code image"
              />
            </div>

            <div ref={readerWrapRef} className="scanner-reader-wrap">
              <div className="scanner-reader-frame" data-active={scanning} aria-hidden={!scanning}>
                <div id={readerId} className="scanner-reader" />
                {showReaderPlaceholder && (
                  <div className="scanner-reader-placeholder">
                    <div className="scanner-reader-placeholder-inner">
                      <FiCamera className="scanner-reader-placeholder-icon" aria-hidden />
                      <p>Camera preview appears here</p>
                      <p className="scanner-reader-hint">Use a well-lit area and hold steady</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="scanner-manual">
              <label htmlFor="manual-booking" className="scanner-manual-label">
                Booking ID
              </label>
              <div className="scanner-manual-row">
                <input
                  id="manual-booking"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  placeholder="e.g. BK1927EAA1"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && onManualVerify()}
                />
                <button
                  type="button"
                  className="scanner-btn scanner-btn--primary"
                  onClick={onManualVerify}
                  disabled={loading || !manualId.trim()}
                >
                  Verify
                </button>
              </div>
            </div>
          </div>

          {loading && (
            <div className="scanner-inline-status" role="status" aria-live="polite">
              <FiLoader className="scanner-spinner" aria-hidden />
              <span>Verifying ticket…</span>
            </div>
          )}
        </section>
      </div>

      {modalOpen && (
        <div
          className="scanner-modal-backdrop"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="scanner-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="scanner-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="scanner-modal-header">
              <h2 id="scanner-modal-title" className="scanner-modal-title">
                Ticket check
              </h2>
              <button
                ref={modalCloseRef}
                type="button"
                className="scanner-modal-close"
                onClick={closeModal}
                aria-label="Close dialog"
              >
                <FiX aria-hidden />
              </button>
            </div>

            <div className="scanner-modal-body scanner-modal-body--flow">
              {error && result && (
                <div className="scanner-popup-inline-error" role="alert">
                  {error}
                </div>
              )}
              {(error && !result) || result ? (
                <ScannerValidationPopup
                  result={result}
                  booking={booking}
                  error={error}
                  hasErrorOnly={Boolean(error && !result)}
                  showAccept={showAccept}
                  showExpiredBtn={showExpiredBtn}
                  actionLoading={actionLoading}
                  onApprove={markVisited}
                  onMarkExpired={markExpired}
                  onClose={closeModal}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
